<template>
  <slot
    name="content"
    :filterChange="filterChange"
    :setDestinationData="setDestinationData"
    :filterRef="filterRef"
    :showMoreHandler="showMoreHandler"
    :isLessThanDesktop="isLessThanDesktop"
  ></slot>
  <slot
    name="dealsmodal"
    :destinationData="destinationData"
    :destinationModalOpen="destinationModalOpen"
    :closeDestinationModal="closeDestinationModal"
    :closeVisaModal="closeVisaModal"
    :closeBothModals="closeBothModals"
    :openVisaModal="openVisaModal"
  ></slot>
  <slot
    name="visamodal"
    :destinationData="destinationData"
    :destinationModalOpen="destinationModalOpen"
    :openDestinationModal="openDestinationModal"
    :closeDestinationModal="closeDestinationModal"
    :closeVisaModal="closeVisaModal"
    :closeBothModals="closeBothModals"
  ></slot>
</template>

<script setup>
import { ref, provide, onMounted, watch, onBeforeMount } from 'vue';
import { useIsBreakpointSize } from '../../hooks/useIsMobile';
import { useCookies } from '../../hooks/useCookies';

const props = defineProps({
  defaultCardCount: {
    type: Number,
    default: 8,
  },
  onLoadLabel: {
    type: String,
    default: '',
  },
  onLoadIata: {
    type: String,
    default: '',
  },
});

const isLessThanDesktop = useIsBreakpointSize(991);
const { getCookie } = useCookies();
const filterRef = ref({});
const pageNumber = ref(0);
const destinationData = ref({});
const destinationModalOpen = ref(false);
const visaModalOpen = ref(false);
const urlParamNames = {
  from: 'originIataCode',
  month: 'flightDealsMonth',
  fare: 'flightDealsFare',
  region: 'flightDealsRegion',
  activities: 'flightDealsActivity',
};

const currentFromValue = ref('');

provide('injectedIsOpenDeals', destinationModalOpen);
provide('injectedIsOpenVisa', visaModalOpen);

const filterChange = (a) => {
  // Remove the city name before writing it to the url

  filterRef.value = { ...a };
  a.from = { locIATA: a.from.locIATA };
  const locChanged = a?.from?.locIATA !== currentFromValue.value;

  for (const key in urlParamNames) {
    if (urlParamNames.hasOwnProperty(key)) {
      const isString = typeof a[key] === 'string';
      const isArray = Array.isArray(a[key]);
      const isObject = typeof a[key] === 'object' && !isArray;
      if (isString) {
        writeURLParamHandler(urlParamNames[key], a[key]);
      }

      if (isObject) {
        const objValues = Object.values(a[key]);
        let objString = objValues.toString();

        writeURLParamHandler(urlParamNames[key], objString);
      }

      if (isArray) {
        const stringFromArray = a[key].join('|');
        writeURLParamHandler(urlParamNames[key], stringFromArray);
      }
    }
  }
  if (locChanged) {
    removeURLParamHandler('flightDealsFare');
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }
};

const readURLParamHandler = (paramName) => {
  const urlParams = new URLSearchParams(window.location.search);
  const paramValue = urlParams.get(paramName);
  return paramValue;
};

const writeURLParamHandler = (paramName, paramValue) => {
  const url = new URL(window.location);
  url.searchParams.set(paramName, paramValue);
  window.history.pushState({}, '', url);
};

const removeURLParamHandler = (paramName) => {
  const url = new URL(window.location);
  url.searchParams.delete(paramName);
  window.history.pushState({}, '', url);
};

const loadURLParamsHandler = () => {
  const current = { ...filterRef.value };
  current.from = {
    locIATA: props.onLoadIata ? props.onLoadIata : '',
    locCity: props.onLoadLabel ? props.onLoadLabel : '',
  };
  const urlParamsFlightDealsMonth = readURLParamHandler('flightDealsMonth');
  const monthValues = urlParamsFlightDealsMonth
    ? urlParamsFlightDealsMonth?.split(',')
    : [];

  const monthObj = {
    minValue: parseInt(monthValues[0]) || '',
    maxValue: parseInt(monthValues[1]) || '',
  };
  current.month = { ...monthObj };

  const urlParamsFareValues = readURLParamHandler('flightDealsFare');
  const fareValues = urlParamsFareValues ? urlParamsFareValues?.split(',') : [];
  const fareObj = {
    minValue: fareValues[0] || '',
    maxValue: fareValues[1] || '',
  };
  current.fare = { ...fareObj };

  const urlParamsDealsRegion = readURLParamHandler('flightDealsRegion');
  current.region = urlParamsDealsRegion
    ? [...urlParamsDealsRegion.split('|')]
    : [];

  const urlParamstDealsActivity = readURLParamHandler('flightDealsActivity');
  current.activities = urlParamstDealsActivity
    ? [...urlParamstDealsActivity.split('|')]
    : [];

  filterRef.value = { ...current };

  currentFromValue.value = current.from.locIATA;
};

const getAllCardItems = () => {
  return [
    ...document.querySelectorAll('.flight-deals__container .cta--destination'),
  ];
};
const getFilteredCardItems = () => {
  return [
    ...document.querySelectorAll(
      '.flight-deals__container .cta--destination[data-matches-filters="true"]',
    ),
  ];
};

const setCardsDisplay = () => {
  const allCards = getAllCardItems();
  const filteredCards = getFilteredCardItems();

  allCards.forEach((el) => el.parentElement.classList.add('display--none'));

  const showCardCount = pageNumber.value * props.defaultCardCount;
  filteredCards.forEach((el, i) => {
    if (i < showCardCount) {
      el.parentElement.classList.remove('display--none');
    }
  });
  toggleShowMoreHandler(filteredCards.length > showCardCount);
};

const activityCheck = (activities) => {
  return (
    !filterRef?.value?.activities?.length ||
    filterRef.value.activities.some((activity) =>
      activities.toLowerCase().split(',').includes(activity),
    )
  );
};
const regionCheck = (region) => {
  return (
    !filterRef?.value?.region?.length ||
    filterRef.value.region.some(
      (filterRegion) => region.toLowerCase() === filterRegion,
    )
  );
};
const fareCheck = (fare) => {
  fare = fare.replace(',', '.');
  const isMoreThanMin = filterRef?.value?.fare?.minValue
    ? fare >= parseInt(filterRef.value.fare.minValue)
    : true;
  const isLessThanMax = filterRef?.value?.fare?.maxValue
    ? fare <= parseInt(filterRef.value.fare.maxValue)
    : true;
  return isMoreThanMin && isLessThanMax;
};
const monthCheck = (month) => {
  const isMoreThanMin = filterRef?.value?.month?.minValue
    ? month >= parseInt(filterRef.value.month.minValue)
    : true;
  const isLessThanMax = filterRef?.value?.month?.maxValue
    ? month <= parseInt(filterRef.value.month.maxValue)
    : true;
  return isMoreThanMin && isLessThanMax;
};

const filterDealsHandler = () => {
  const allItems = getAllCardItems();
  allItems.forEach((el) => el.setAttribute('data-matches-filters', ''));
  const noDealsMsg = document.getElementById('deals-no-deals');

  const filteredItems = allItems.filter((item) => {
    const { regions, activities, month, fare } = item.dataset;
    return (
      activityCheck(activities) &&
      regionCheck(regions) &&
      monthCheck(month) &&
      fareCheck(fare)
    );
  });

  filteredItems.forEach((el) => {
    el.setAttribute('data-matches-filters', 'true');
  });

  if (!noDealsMsg) {
    return;
  }
  if (filteredItems.length === 0) {
    noDealsMsg.style = 'display: block;';
  } else {
    noDealsMsg.style = 'display: none;';
  }
};

watch(
  () => filterRef.value,
  () => {
    filterDealsHandler();
    setCardsDisplay();
  },
);

const setDestinationData = (data) => {
  destinationData.value = { ...data };
  openDestinationModal();
  visaModalOpen.value = false;
};

const openDestinationModal = () => {
  destinationModalOpen.value = true;
};
const closeDestinationModal = () => {
  destinationModalOpen.value = false;
};

const openVisaModal = () => {
  destinationModalOpen.value = false;
  visaModalOpen.value = true;
};

const closeVisaModal = () => {
  visaModalOpen.value = false;
};

const closeBothModals = () => {
  destinationModalOpen.value = false;
  visaModalOpen.value = false;
};

const showMoreHandler = () => {
  pageNumber.value += 1;
  setCardsDisplay();
};

const toggleShowMoreHandler = (bool) => {
  const showmore = document.getElementById('deals-showmore');
  if (!showmore) {
    return;
  }
  if (bool) {
    showmore.style.display = 'block';
  } else {
    showmore.style.display = 'none';
  }
};

onBeforeMount(() => {
  loadURLParamsHandler();
});

const injectedFilterState = ref({});
provide('injectedFilterState', injectedFilterState);

watch(filterRef, () => {
  injectedFilterState.value = { ...filterRef.value };
});

onMounted(() => {
  showMoreHandler();
});
</script>
